import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';

interface AddMaterielElectriqueProps {
  t: any;
  isRTL: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface MaterielElectriqueProduct {
  name: string;
  title?: string;
  mainImage: string;
  images?: string[];
  category: string;
  subcategory: string;
  description?: string;
  characteristics?: { [key: string]: string };
  additionalImages: string[];
}

// Simplified table structure
interface SimpleTable {
  id: string;
  name: string;
  data: { [key: string]: string }[];
}

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

// Much larger modal
const ModalContent = styled(motion.div)<{ $isRTL: boolean }>`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  max-width: 1400px;
  width: 95%;
  max-height: 95vh;
  overflow-y: auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const ModalTitle = styled.h2<{ $isRTL: boolean }>`
  font-size: 2rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormSection = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const FormGroup = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Label = styled.label<{ $isRTL: boolean }>`
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Input = styled.input<{ $isRTL: boolean }>`
  padding: 1rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select<{ $isRTL: boolean }>`
  padding: 1rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.2s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea<{ $isRTL: boolean }>`
  padding: 1rem;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FileInput = styled.input<{ $isRTL: boolean }>`
  padding: 1rem;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.3s ease;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

// Simplified table components
const TablesSection = styled.div<{ $isRTL: boolean }>`
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const TableCard = styled.div`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 2rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.div`
  background: #374151;
  color: white;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableTitle = styled.h4`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const TableControls = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' | 'success' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          &:hover { background: #2563eb; }
        `;
      case 'success':
        return `
          background: #10b981;
          color: white;
          &:hover { background: #059669; }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover { background: #dc2626; }
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
          border: 2px solid #d1d5db;
          &:hover { background: #e5e7eb; }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1rem;
`;

const TableHead = styled.thead`
  background: #f3f4f6;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  
  &:hover {
    background: #f9fafb;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-right: 1px solid #e5e7eb;
  
  &:last-child {
    border-right: none;
  }
`;

const TableDataCell = styled.td`
  padding: 1rem;
  border-right: 1px solid #e5e7eb;
  
  &:last-child {
    border-right: none;
  }
`;

const TableInput = styled.input`
  width: 100%;
  border: 1px solid #d1d5db;
  background: white;
  padding: 0.75rem;
  font-size: 1rem;
  border-radius: 6px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const AddTableButton = styled(Button)`
  margin-bottom: 2rem;
  padding: 1rem 2rem;
  font-size: 1.125rem;
`;

const ImagePreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const ImagePreview = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 150px;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(220, 38, 38, 1);
  }
`;

const UploadProgress = styled.div<{ $progress: number }>`
  width: 100%;
  height: 12px;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 0.5rem;

  &::after {
    content: '';
    display: block;
    width: ${props => props.$progress}%;
    height: 100%;
    background: #3b82f6;
    transition: width 0.3s ease;
  }
`;

const ProgressText = styled.div`
  font-size: 1rem;
  color: #6b7280;
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 3rem;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid #f3f4f6;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AddMaterielElectrique: React.FC<AddMaterielElectriqueProps> = ({ t, isRTL, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<MaterielElectriqueProduct>({
    name: '',
    title: '',
    mainImage: '',
    images: [],
    category: '2',
    subcategory: '',
    description: '',
    characteristics: {},
    additionalImages: []
  });

  // Simplified table state
  const [tables, setTables] = useState<SimpleTable[]>([]);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);
  const [mainImagePreview, setMainImagePreview] = useState<string | null>(null);
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState<string[]>([]);

  const subcategories = [
    { value: 'solutions', label: 'Solutions' },
    { value: 'isolateurs-composites', label: 'Isolateurs Composites HTA / HTB' },
    { value: 'accessoires-chaines', label: 'Accessoires pour Chaînes d\'Isolateurs' },
    { value: 'interrupteur-aerien', label: 'Interrupteur Aérien à Commande Manuelle IACM' },
    { value: 'accessoires-reseaux-bt', label: 'Accessoires pour Réseaux B.T' },
    { value: 'accessoires-protection', label: 'Accessoires de protection et raccordement' },
    { value: 'ferrure-armement', label: 'Ferrure et Armement' },
    { value: 'eclairage-public', label: 'Eclairage Public' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMainImageFile(file);
      setFormData(prev => ({ ...prev, mainImage: file.name })); // Set the mainImage field
      const reader = new FileReader();
      reader.onload = (e) => {
        setMainImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setAdditionalImageFiles(prev => [...prev, ...files]);
      
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setAdditionalImagePreviews(prev => [...prev, e.target?.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeMainImage = () => {
    setMainImageFile(null);
    setMainImagePreview(null);
    setFormData(prev => ({ ...prev, mainImage: '' })); // Clear the mainImage field
  };

  const removeAdditionalImage = (index: number) => {
    setAdditionalImageFiles(prev => prev.filter((_, i) => i !== index));
    setAdditionalImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Simplified table functions
  const addTable = () => {
    const newTable: SimpleTable = {
      id: Date.now().toString(),
      name: `Tableau ${tables.length + 1}`,
      data: [
        { 'Colonne 1': '', 'Colonne 2': '', 'Colonne 3': '' }
      ]
    };
    setTables([...tables, newTable]);
  };

  const removeTable = (tableId: string) => {
    setTables(tables.filter(table => table.id !== tableId));
  };

  const updateTableName = (tableId: string, name: string) => {
    setTables(tables.map(table => 
      table.id === tableId ? { ...table, name } : table
    ));
  };

  const addRow = (tableId: string) => {
    setTables(tables.map(table => 
      table.id === tableId 
        ? {
            ...table,
            data: [...table.data, Object.fromEntries(
              Object.keys(table.data[0] || {}).map(key => [key, ''])
            )]
          }
        : table
    ));
  };

  const removeRow = (tableId: string, rowIndex: number) => {
    setTables(tables.map(table => 
      table.id === tableId 
        ? {
            ...table,
            data: table.data.filter((_, index) => index !== rowIndex)
          }
        : table
    ));
  };

  const addColumn = (tableId: string) => {
    setTables(tables.map(table => 
      table.id === tableId 
        ? {
            ...table,
            data: table.data.map(row => ({
              ...row,
              [`Colonne ${Object.keys(row).length + 1}`]: ''
            }))
          }
        : table
    ));
  };

  const removeColumn = (tableId: string, columnName: string) => {
    setTables(tables.map(table => 
      table.id === tableId 
        ? {
            ...table,
            data: table.data.map(row => {
              const newRow = { ...row };
              delete newRow[columnName];
              return newRow;
            })
          }
        : table
    ));
  };

  const updateCellValue = (tableId: string, rowIndex: number, columnName: string, value: string) => {
    setTables(tables.map(table => 
      table.id === tableId 
        ? {
            ...table,
            data: table.data.map((row, index) => 
              index === rowIndex 
                ? { ...row, [columnName]: value }
                : row
            )
          }
        : table
    ));
  };

  const updateColumnName = (tableId: string, oldName: string, newName: string) => {
    setTables(tables.map(table => 
      table.id === tableId 
        ? {
            ...table,
            data: table.data.map(row => {
              const newRow = { ...row };
              newRow[newName] = newRow[oldName];
              delete newRow[oldName];
              return newRow;
            })
          }
        : table
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if required fields are filled
    if (!formData.name || !formData.subcategory || !mainImageFile) {
      alert('Veuillez remplir tous les champs obligatoires (nom, sous-catégorie, et image principale)');
      return;
    }

    setLoading(true);
    setIsUploading(true);

    try {
      // Upload main image
      let mainImageUrl = '';
      if (mainImageFile) {
        mainImageUrl = await FirebaseService.uploadImage(mainImageFile, (progress) => {
          setUploadProgress(progress);
        });
      }

      // Upload additional images
      const additionalImageUrls: string[] = [];
      for (const file of additionalImageFiles) {
        const url = await FirebaseService.uploadImage(file, (progress) => {
          setUploadProgress(progress);
        });
        additionalImageUrls.push(url);
      }

      // Convert tables to characteristics
      const characteristicsObj: { [key: string]: any } = {};
      tables.forEach(table => {
        characteristicsObj[table.name] = {
          type: 'table',
          data: table.data
        };
      });

      const productData = {
        ...formData,
        mainImage: mainImageUrl,
        additionalImages: additionalImageUrls,
        characteristics: characteristicsObj,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await FirebaseService.createProduct(productData);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Erreur lors de la création du produit');
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        $isRTL={isRTL}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader $isRTL={isRTL}>
          <ModalTitle $isRTL={isRTL}>Ajouter un Produit</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle $isRTL={isRTL}>Informations Générales</SectionTitle>
            
            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Nom du produit *</Label>
              <Input
                $isRTL={isRTL}
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Nom du produit"
                required
              />
            </FormGroup>

            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Titre</Label>
              <Input
                $isRTL={isRTL}
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Titre du produit"
              />
            </FormGroup>

            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Catégorie *</Label>
              <Select
                $isRTL={isRTL}
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                <option value="2">Matériel Électrique</option>
              </Select>
            </FormGroup>

            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Sous-catégorie *</Label>
              <Select
                $isRTL={isRTL}
                name="subcategory"
                value={formData.subcategory}
                onChange={handleInputChange}
                required
              >
                <option value="">Sélectionner une sous-catégorie</option>
                {subcategories.map(sub => (
                  <option key={sub.value} value={sub.value}>
                    {sub.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Description</Label>
              <TextArea
                $isRTL={isRTL}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Description du produit"
              />
            </FormGroup>
          </FormSection>

          <FormSection>
            <SectionTitle $isRTL={isRTL}>Images</SectionTitle>
            
            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Image principale *</Label>
              <FileInput
                $isRTL={isRTL}
                type="file"
                accept="image/*"
                onChange={handleMainImageChange}
              />
              {mainImagePreview && (
                <ImagePreviewContainer>
                  <ImagePreview>
                    <img src={mainImagePreview} alt="Main preview" />
                    <RemoveImageButton type="button" onClick={removeMainImage}>
                      ×
                    </RemoveImageButton>
                  </ImagePreview>
                </ImagePreviewContainer>
              )}
            </FormGroup>

            <FormGroup $isRTL={isRTL}>
              <Label $isRTL={isRTL}>Images supplémentaires</Label>
              <FileInput
                $isRTL={isRTL}
                type="file"
                accept="image/*"
                multiple
                onChange={handleAdditionalImagesChange}
              />
              {additionalImagePreviews.length > 0 && (
                <ImagePreviewContainer>
                  {additionalImagePreviews.map((preview, index) => (
                    <ImagePreview key={index}>
                      <img src={preview} alt={`Additional preview ${index + 1}`} />
                      <RemoveImageButton 
                        type="button" 
                        onClick={() => removeAdditionalImage(index)}
                      >
                        ×
                      </RemoveImageButton>
                    </ImagePreview>
                  ))}
                </ImagePreviewContainer>
              )}
            </FormGroup>
          </FormSection>

          <FormSection>
            <SectionTitle $isRTL={isRTL}>Caractéristiques Techniques</SectionTitle>
            
            <TablesSection $isRTL={isRTL}>
              <AddTableButton type="button" onClick={addTable} $variant="success">
                + Ajouter un tableau
              </AddTableButton>

              {tables.map((table) => (
                <TableCard key={table.id}>
                  <TableHeader>
                    <TableTitle>
                      <TableInput
                        value={table.name}
                        onChange={(e) => updateTableName(table.id, e.target.value)}
                        style={{ background: 'transparent', color: 'white', border: 'none', fontSize: '1.25rem' }}
                      />
                    </TableTitle>
                    <TableControls>
                      <Button type="button" onClick={() => addColumn(table.id)}>
                        + Colonne
                      </Button>
                      <Button type="button" onClick={() => addRow(table.id)}>
                        + Ligne
                      </Button>
                      <Button type="button" $variant="danger" onClick={() => removeTable(table.id)}>
                        Supprimer
                      </Button>
                    </TableControls>
                  </TableHeader>

                  <StyledTable>
                    <TableHead>
                      <TableRow>
                        {Object.keys(table.data[0] || {}).map((columnName) => (
                          <TableHeaderCell key={columnName}>
                            <TableInput
                              value={columnName}
                              onChange={(e) => updateColumnName(table.id, columnName, e.target.value)}
                              placeholder="Nom de la colonne"
                            />
                            {Object.keys(table.data[0] || {}).length > 1 && (
                              <Button
                                type="button"
                                $variant="danger"
                                onClick={() => removeColumn(table.id, columnName)}
                                style={{ marginTop: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              >
                                Supprimer colonne
                              </Button>
                            )}
                          </TableHeaderCell>
                        ))}
                        <TableHeaderCell>Actions</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {table.data.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {Object.entries(row).map(([columnName, value]) => (
                            <TableDataCell key={columnName}>
                              <TableInput
                                value={value}
                                onChange={(e) => updateCellValue(table.id, rowIndex, columnName, e.target.value)}
                                placeholder="Valeur"
                              />
                            </TableDataCell>
                          ))}
                          <TableDataCell>
                            <Button
                              type="button"
                              $variant="danger"
                              onClick={() => removeRow(table.id, rowIndex)}
                              disabled={table.data.length <= 1}
                            >
                              Supprimer
                            </Button>
                          </TableDataCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </TableCard>
              ))}
            </TablesSection>
          </FormSection>

          {isUploading && (
            <FormSection>
              <UploadProgress $progress={uploadProgress} />
              <ProgressText>Upload des images... {uploadProgress}%</ProgressText>
            </FormSection>
          )}

          <ButtonGroup>
            <Button type="button" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" $variant="primary" disabled={loading}>
              {loading && <LoadingSpinner />}
              {loading ? 'Création...' : 'Créer le produit'}
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddMaterielElectrique;